import { useState, useEffect, useRef } from 'react';
import { useWeb3React } from '@web3-react/core';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Typography from '@material-ui/core/Typography';
import LendBorrowHOC from 'hoc/LendBorrowHOC';
import FilterPanel from 'parts/Lend/FilterPanel';
import AssetCard from 'components/AssetCard';
import AssetRow from 'components/AssetRow';
import LoadingBar from 'components/UI/LoadingBar';
import ToggleView from 'components/ToggleView';
import usePersistState from 'hooks/usePersistState';
import { getAssetsByQuery } from 'services';
import { getManagedAssets } from 'services/nftfi';
import { COUNTS_ON_PAGE } from 'config/settings';
import { isEmpty } from 'utils/utility';
import { ViewMode } from 'constants/types';

function LendPage({}) {
  const context = useWeb3React();
  const { account } = context;

  const [viewMode, setViewMode] = usePersistState(ViewMode.TABLE, 'asset-view-mode');
  const [assetList, setAssetList] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [page, setPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(true);
  const loader = useRef(null);
  const [cursor, setCursor] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [totalListedCount, setTotalListedCount] = useState(0);
  const [visibleListedOnly, setVisibleListedOnly] = useState(false);
  const [stringTraits, setStringTraits] = useState([]);
  const [numericTraits, setNumericTraits] = useState([]);
  const [filterFlag, setFilterFlag] = useState();

  // listed
  const [listedAssetArray, setListedAssetArray] = useState([]);
  const [loadingListedAssets, setLoadingListedAssets] = useState(false);
  const [listedPage, setListedPage] = useState(0);
  const [hasNextListedPage, setHasNextListedPage] = useState(true);

  // filtering
  const [filterStringTraits, setFilterStringTraits] = useState([]);
  const [filterNumericTraits, setFilterNumericTraits] = useState([]);

  const [displayAssetArray, setDisplayAssetArray] = useState([]);

  const fetchListedAssets = async () => {
    if (loadingListedAssets) {
      return;
    }
    try {
      if (!hasNextListedPage) {
        return;
      }
      setLoadingListedAssets(true);

      const { data: result, total } = await getManagedAssets({
        account,
        status: null,
        offset: listedAssetArray.length,
      });

      setTotalListedCount(total);

      if (result.length === 0) {
        setHasNextListedPage(false);
      } else if (result.length < COUNTS_ON_PAGE) {
        setHasNextListedPage(false);
      }
      setListedAssetArray([...listedAssetArray, ...result]);
    } catch (err) {
      console.log(err);
    }
    setLoadingListedAssets(false);
  };

  const fetchAssets = async () => {
    // if (loadingAssets || loadingListedAssets) {
    //   return;
    // }
    try {
      if (!hasNextPage) {
        return;
      }
      setLoadingAssets(true);
      const result = await getAssetsByQuery({
        first: COUNTS_ON_PAGE,
        cursor: cursor,
        stringTraits: filterStringTraits,
        numericTraits: filterNumericTraits,
      });

      const assetCount = result?.query?.search?.totalCount || 0;
      if (totalCount !== assetCount) {
        setTotalCount(assetCount);
      }

      if (result?.query?.search?.edges?.length > 0) {
        const newAssets = result.query.search.edges.map(item => item.node.asset);
        if (cursor) {
          setAssetList(assetList => [...assetList, ...newAssets]);
        } else {
          setAssetList(newAssets);
        }
      }

      await setCursor(result?.query?.search?.pageInfo?.endCursor || '');
      setHasNextPage(result?.query?.search?.pageInfo?.hasNextPage);

      if (stringTraits.length === 0) {
        setStringTraits(result?.query?.collection?.stringTraits);
      }
      if (numericTraits.length === 0) {
        setNumericTraits(result?.query?.collection?.numericTraits);
      }
    } catch (err) {
      console.log(err);
    }
    setLoadingAssets(false);
  };

  const setFilterStringTraitsHandler = value => {
    setFilterStringTraits(value);
    setFilterFlag(!filterFlag);
    setCursor('');
    setHasNextPage(true);
  };

  const setFilterNumericTraitsHandler = value => {
    setFilterNumericTraits(value);
    setFilterFlag(!filterFlag);
    setCursor('');
    setHasNextPage(true);
  };

  const handleObserver = entities => {
    const target = entities[0];
    // TODO: check if hasNextPage working or not
    if (target.isIntersecting && hasNextPage) {
      setPage(page => page + 1);
    }
    if (target.isIntersecting && hasNextListedPage) {
      setListedPage(listedPage => listedPage + 1);
    }
  };

  const handleChangeSwitch = event => {
    setVisibleListedOnly(event.target.checked);
  };

  const options = {
    root: null,
    rootMargin: '20px',
    threshold: 1.0,
  };

  const observer = new IntersectionObserver(handleObserver, options);

  useEffect(() => {
    if (loader.current) {
      observer.observe(loader.current);
    }
    // TODO: check later
    // return (() => {
    //   observer.unobserve(loader.current)
    // });
  }, []);

  useEffect(() => {
    fetchListedAssets();
  }, [filterFlag, listedPage]);

  useEffect(() => {
    if (!(hasNextListedPage && page > 0)) {
      fetchAssets();
    }
  }, [filterFlag, page, hasNextListedPage]);

  useEffect(() => {
    fetchAssets();
  }, []);

  useEffect(() => {
    if (!isEmpty(filterNumericTraits) || !isEmpty(filterStringTraits)) {
      setDisplayAssetArray(assetList);
    } else if (hasNextListedPage || visibleListedOnly) {
      setDisplayAssetArray(listedAssetArray);
    } else {
      const newAssetList = assetList.filter(
        asset =>
          !listedAssetArray.find(listedAsset => listedAsset.nftCollateralId === asset.tokenId),
      );
      setDisplayAssetArray([...listedAssetArray, ...newAssetList]);
    }
  }, [
    assetList.length,
    listedAssetArray.length,
    hasNextListedPage,
    filterFlag,
    filterNumericTraits.length,
    filterStringTraits.length,
    visibleListedOnly,
  ]);

  useEffect(() => {
    if (isEmpty(filterNumericTraits) && isEmpty(filterStringTraits)) {
      setHasNextPage(true);
      setHasNextListedPage(true);
    } else {
      setAssetList([]);
      setHasNextListedPage(false);
    }
  }, [filterFlag, filterNumericTraits.length, filterStringTraits.length]);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <FilterPanel
            stringTraits={stringTraits}
            numericTraits={numericTraits}
            filterStringTraits={filterStringTraits}
            setFilterStringTraits={setFilterStringTraitsHandler}
            filterNumericTraits={filterNumericTraits}
            setFilterNumericTraits={setFilterNumericTraitsHandler}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={8}>
          <Grid container spacing={viewMode === ViewMode.CARD ? 3 : 1} alignItems='stretch'>
            <Grid item xs={12}>
              <Box
                display='flex'
                flexWrap='wrap'
                justifyContent='space-between'
                alignItems='center'>
                <Box mr='auto'>
                  <Typography>
                    Total {totalCount} Result, {totalListedCount} Cryptobonds are listed
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={visibleListedOnly}
                      onChange={handleChangeSwitch}
                      name='Listed Only'
                      color='primary'
                    />
                  }
                  disabled={!isEmpty(filterNumericTraits) || !isEmpty(filterStringTraits)}
                  label='Listed Only'
                />
                <ToggleView viewMode={viewMode} onToggle={setViewMode} />
              </Box>
            </Grid>
            {displayAssetArray.map((asset, index) =>
              viewMode !== ViewMode.CARD ? (
                <Grid key={index} item xs={12}>
                  <AssetRow data={asset} />
                </Grid>
              ) : (
                <Grid
                  key={index}
                  item
                  xs={12}
                  sm={viewMode === ViewMode.CARD ? 6 : 12}
                  md={viewMode === ViewMode.CARD ? 4 : 12}
                  lg={viewMode === ViewMode.CARD ? 3 : 12}>
                  <AssetCard data={asset} />
                </Grid>
              ),
            )}
            {(loadingAssets || loadingListedAssets) && (
              <Grid item xs={12} key='loading' align='center'>
                <LoadingBar />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
      <div ref={loader} />
    </>
  );
}

export default LendBorrowHOC(LendPage, true);
